import { memo, useEffect, useState } from "react";
import { useContent } from "../../../../../contexts/contentContext/contentContext";
import useCreditCard from "../../hooks/useCreditCard";
import Button, {
  ButtonAccessability,
} from "../../../../Elements/Button/Button";
import parse from "html-react-parser";
import RadioElement from "../../../../Elements/RadioElement/RadioElement";
import Image from "../../../../Elements/Image/Image";
import CreditCardDetails from "./components/CreditCardDetails";
import usePurchases from "../../hooks/usePurchases";
import { PaymentTypeE, PurchaseStepE } from "../../models/enums";
import makeContactUsLink from "../../helpers/makeContactUsLink";
import PaymentTypeList from "./components/PaymentTypeList";
import { useUser } from "../../../../../contexts/userContext/userContext";
import SectionTitle from "../SectionTitle";
import LoadingImageElement from "../../../../Elements/LoadingContainerElement/LoadingImageElement";
import useAddon from "../../hooks/useAddon";
import { useParams } from "react-router-dom";
import type { PurchaseParamsT } from "../../models/models";
import ConditionalTooltip from "../../../../Reusable/ConditionalTooltip/ConditionalTooltip";
import useCouponCode from "../../hooks/useCouponCode";
import useCurrentTokenId from "../../hooks/useCurrentTokenId";

import styles from "./styles.module.scss";
import SelectedPaymentMethodSection from "./components/SelectedPaymentMethodSection";

const PaymentMethodSection = memo(() => {
  const { packageType } = useParams<PurchaseParamsT>();
  const {
    creditCards,
    paymentType,
    setPaymentType,
    selectedCreditCardId,
    fetchCreditCards,
    isCreditCardsLoading,
    clearCreditCardList,
  } = useCreditCard();
  const { completedStep, goToSecondStep } = usePurchases();
  const { getTextByKey, getPath } = useContent();
  const [showPaymentTypeList, setShowPaymentTypeList] = useState(false);
  const { isLoggedIn } = useUser();
  const { addonQuantity, existingAddonQuantity } = useAddon();
  const { couponCode } = useCouponCode();
  const tokenId = useCurrentTokenId();

  // Show list if credit cards data loaded and no one card found
  useEffect(() => {
    if (!isCreditCardsLoading && !selectedCreditCardId) {
      setShowPaymentTypeList(true);
    }
  }, [isCreditCardsLoading]);

  useEffect(() => {
    if (!isLoggedIn()) {
      // Clear credit cards data if user logged out
      clearCreditCardList();
    }
    fetchCreditCards(
      packageType === "sub-users" || packageType === "extra-storage"
    );
  }, [isLoggedIn()]);

  return completedStep < PurchaseStepE.FIRST_PURCHASE_METHOD_STEP ? (
    <div className={styles.purchasePaymentSection}>
      {!isCreditCardsLoading ? (
        <div
          className={`${styles.paymentSectionStep1} ${styles.paymentSectionStep}`}
        >
          <SectionTitle titleTextKey="purchase-payment-method-title" step={1} />
          <div className={styles.paymentSectionBody}>
            <div className={styles.choosePaymentType}>
              <div className={styles.choosePaymentText}>
                <span>{getTextByKey("choose-payment-text")}</span>
              </div>
              {isCreditCardsLoading ? (
                <Image name="preloader" ext="gif" className={styles.loader} />
              ) : null}
              {!isCreditCardsLoading && creditCards?.length > 0 ? (
                // Show Selected payment method if credit cards loaded
                // and at least one card found and selected
                // or payment type equal to PayPal
                // or user wants to use a new credit card.
                paymentType === PaymentTypeE.PAYPAL ||
                selectedCreditCardId ||
                paymentType === PaymentTypeE.NEW_CREDIT_CARD ? (
                  <>
                    <CreditCardDetails
                      creditCardId={selectedCreditCardId}
                      paymentType={paymentType!}
                    />
                    {!showPaymentTypeList &&
                    completedStep < PurchaseStepE.FIRST_PURCHASE_METHOD_STEP ? (
                      <ButtonAccessability
                        className={styles.changePaymentMethod}
                        onClick={() => setShowPaymentTypeList(true)}
                      >
                        {getTextByKey("change-payment-method")}
                      </ButtonAccessability>
                    ) : null}
                  </>
                ) : null
              ) : !isCreditCardsLoading ? (
                <div className={styles.paymentRadios}>
                  <RadioElement
                    specificClass={styles.firstRadio}
                    state={paymentType}
                    setState={setPaymentType}
                    fieldToCheck={PaymentTypeE.CREDIT_CARD}
                    textRadioObject={getTextByKey("credit-card-text")}
                    tooltipRadio={"credit-card-tooltip"}
                  />
                  {!tokenId ? (
                    <RadioElement
                      state={paymentType}
                      setState={setPaymentType}
                      fieldToCheck={PaymentTypeE.PAYPAL}
                      textRadioObject={
                        <Image name="purchases/paypal-button" ext="png" />
                      }
                      tooltipRadio={"paypal-tooltip"}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className={styles.contactUsePayments}>
              <span>
                {parse(
                  makeContactUsLink(
                    getTextByKey("choose-payment-contact-us"),
                    getPath()
                  )
                )}
              </span>
            </div>
            {showPaymentTypeList ? (
              // Using inline styles for hiding and showing the list
              // in order to make images loads once and prevent blinking
              <div style={{ display: showPaymentTypeList ? "block" : "none" }}>
                <PaymentTypeList
                  setShowPaymentTypeList={setShowPaymentTypeList}
                />
              </div>
            ) : null}
          </div>
          {completedStep < PurchaseStepE.FIRST_PURCHASE_METHOD_STEP ? (
            <div className={styles.purchaseContinueBtnWrapper}>
              <ConditionalTooltip
                tooltipTextKey="addon-same-quantity-tooltip"
                condition={
                  packageType === "sub-users" || packageType === "extra-storage"
                    ? addonQuantity === existingAddonQuantity
                    : false
                }
              >
                <Button
                  className={
                    styles.newButton + " " + styles.purchaseContinueBtn
                  }
                  onClick={goToSecondStep}
                  disabled={
                    // JW-2218: Disable continue button if during purchasing addons
                    // user selected the same quantity a existing.
                    (packageType === "sub-users" ||
                    packageType === "extra-storage"
                      ? addonQuantity === existingAddonQuantity
                      : false) ||
                    // Check if coupon code is valid if exists
                    couponCode.code
                      ? !couponCode.isValid
                      : false
                  }
                >
                  {getTextByKey(
                    selectedCreditCardId! > 0 ? "place-order" : "purchase-next"
                  )}
                </Button>
              </ConditionalTooltip>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          <LoadingImageElement />
        </div>
      )}
    </div>
  ) : (
    <SelectedPaymentMethodSection
      setShowPaymentTypeList={setShowPaymentTypeList}
    />
  );
});

export default PaymentMethodSection;
